import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Button,
  Chip,
} from '@mui/material';
import { Psychology, TrendingUp } from '@mui/icons-material';
import { GlassmorphicCard } from '../GlassmorphicCard/GlassmorphicCard';
import PreferencesQuizModal from '../PreferencesQuizModal/PreferencesQuizModal';

interface PersonalizeCardProps {
  className?: string;
}

const PersonalizeCard: React.FC<PersonalizeCardProps> = ({ className }) => {
  const [hasCompletedQuiz, setHasCompletedQuiz] = useState(false);
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);

  // Mock preference data - will be replaced with real data from API
  const completedPreferences = {
    cuisine: ['Italian', 'Japanese', 'Mexican'],
    activities: ['Museums', 'Parks', 'Live Music'],
    priceRange: '$$-$$$',
    completedAt: new Date('2024-01-15'),
  };

  const handleStartQuiz = () => {
    setIsQuizModalOpen(true);
  };

  const handleRetakeQuiz = () => {
    setIsQuizModalOpen(true);
  };

  const handleQuizComplete = () => {
    setHasCompletedQuiz(true);
    setIsQuizModalOpen(false);
  };

  const handleCloseQuiz = () => {
    setIsQuizModalOpen(false);
  };

  return (
    <GlassmorphicCard className={`personalize-card ${className || ''}`}>
      <Box p={3}>
        {/* Header */}
        <Box display="flex" alignItems="center" mb={2}>
          <Psychology
            sx={{
              fontSize: 28,
              background: 'linear-gradient(135deg, #f06ef5, #9c88ff)',
              color: 'transparent',
              backgroundClip: 'text',
              mr: 1.5,
            }}
          />
          <Typography variant="h6" fontWeight={600}>
            Discover My Taste
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {!hasCompletedQuiz ? (
          /* First-time experience */
          <Box>
            <Typography variant="body1" mb={2}>
              Help us personalize your recommendations with a quick taste test!
              Swipe through NYC's best venues and let us know what catches your eye.
            </Typography>

            <Typography variant="body2" color="text.secondary" mb={3}>
              This takes about a minute and makes your planning experience much better.
            </Typography>

            <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={3}>
              <TrendingUp fontSize="small" color="success" />
              <Typography variant="body2" color="success.main">
                Personalized recommendations improve plan quality by 85%
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={handleStartQuiz}
              fullWidth
              className="start-preferences-quiz-button"
              sx={{
                background: 'linear-gradient(135deg, #f06ef5, #9c88ff)',
                color: 'white',
                fontWeight: 600,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(240, 110, 245, 0.4)',
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              Personalize your recommendations
            </Button>
          </Box>
        ) : (
          /* Completed state */
          <Box>
            <Typography variant="body1" mb={2}>
              Great! We've learned your preferences and are personalizing your recommendations.
            </Typography>

            {/* Preference summary */}
            <Box mb={3}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Your favorite cuisines:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {completedPreferences.cuisine.map((cuisine) => (
                  <Chip
                    key={cuisine}
                    label={cuisine}
                    size="small"
                    sx={{
                      background: 'linear-gradient(135deg, #f06ef5, #9c88ff)',
                      color: 'white',
                    }}
                  />
                ))}
              </Box>

              <Typography variant="body2" color="text.secondary" mb={1}>
                Preferred activities:
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {completedPreferences.activities.map((activity) => (
                  <Chip
                    key={activity}
                    label={activity}
                    size="small"
                    sx={{
                      background: 'linear-gradient(135deg, #91EAE4, #86A8E7)',
                      color: 'white',
                    }}
                  />
                ))}
              </Box>

              <Typography variant="caption" color="text.secondary">
                Completed on {completedPreferences.completedAt.toLocaleDateString()}
              </Typography>
            </Box>

            <Button
              variant="outlined"
              onClick={handleRetakeQuiz}
              fullWidth
              sx={{
                borderColor: '#f06ef5',
                color: '#f06ef5',
                fontWeight: 500,
                py: 1,
                borderRadius: 2,
                textTransform: 'none',
                '&:hover': {
                  borderColor: '#9c88ff',
                  background: 'linear-gradient(135deg, rgba(240, 110, 245, 0.1), rgba(156, 136, 255, 0.1))',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Update preferences
            </Button>
          </Box>
        )}
      </Box>

      {/* Quiz Modal */}
      <PreferencesQuizModal
        open={isQuizModalOpen}
        onClose={handleCloseQuiz}
        onComplete={handleQuizComplete}
      />
    </GlassmorphicCard>
  );
};

export default PersonalizeCard;